import { ref, watch } from 'vue';
import type { Router } from 'vue-router';
import { apiFetch } from '@shared/http/client';
import { useAppStateStore } from '@shared/stores/app-state';
import { useUserStore } from '@shared/stores/user';

export interface PushData {
    title: string
    body: string
    appRoute: string
    institutionId?: string
    [key: string]: string | undefined
}

interface FirebaseConfig {
    apiKey: string
    projectId: string
    messagingSenderId: string
    appId: string
}

// The native app only needs the project id: the plugin takes everything else from
// google-services.json / GoogleService-Info.plist, and the backend picks the service account
// by the project id sent along with the token. Web push needs the full web app config.
export type PushFirebaseConfig = Pick<FirebaseConfig, 'projectId'> & Partial<FirebaseConfig>;

function isWebConfig(config: PushFirebaseConfig): config is FirebaseConfig {
    return !!(config.apiKey && config.messagingSenderId && config.appId);
}

const isNative = !!window.kgrCapacitor;
export const PUSH_STORAGE_KEY = 'kgr_push_v3_enabled';

// Source of truth for the push toggle. Push has no backend pref (the notifications
// transformer/PUT only handle email + notify* flags); it lives on the device via the
// FCM token + this flag. Mirror Angular's `enabled$.value !== false`: ON unless the user
// explicitly turned it off — so a fresh/migrated device that inherited a working FCM token
// still shows "on". Reading the (non-existent) backend field instead reset it to off on
// every reload.
export function isPushEnabledStored(): boolean {
    return localStorage.getItem(PUSH_STORAGE_KEY) !== 'false';
}

// The legacy Angular app persisted this same flag via @capacitor/preferences, not plain
// localStorage — native storage on device, `CapacitorStorage.`-prefixed localStorage on web —
// so a device migrated to the Vue app has nothing under our bare key. Read it back through the
// (same-version, default-group) plugin, which resolves to the identical entry on both platforms.
async function readLegacyEnabledFlag(): Promise<string | null> {
    try {
        const { Preferences } = await import('@capacitor/preferences');
        return (await Preferences.get({ key: PUSH_STORAGE_KEY })).value;
    } catch (e) {
        console.error('[usePush] reading legacy push flag failed', e);
        return null;
    }
}

const isWebPushCapable = !isNative && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Module-level singletons — shared across all usePush() calls.
// `supported` follows Angular's WebPushHandler: it starts false on web and only turns true once
// init() has actually confirmed push works (firebase `isSupported()` + a service worker that
// registers and becomes ready). The mere presence of the browser APIs is not enough — the
// notification preferences screen renders "wird für dein System noch nicht unterstützt" off this
// flag, and an optimistic value hides that hint even where push cannot work. Native is always true.
const supported = ref(isNative);
// Mirror of Angular's `PushService.enabled$` (BehaviorSubject<boolean|undefined>): the device flag,
// re-emitted once the async legacy-storage lookup resolves. Views must bind to this rather than
// reading the flag once — on a migrated device the value only lands in localStorage after mount.
const enabled = ref<boolean | undefined>(
    localStorage.getItem(PUSH_STORAGE_KEY) === null ? undefined : localStorage.getItem(PUSH_STORAGE_KEY) === 'true',
);
// eslint-disable-next-line compat/compat -- Notification API used in native apps via Capacitor, not Safari
const permission = ref<NotificationPermission>(typeof Notification !== 'undefined' ? Notification.permission : 'default');
const foregroundMessage = ref<PushData | null>(null);

// Angular PushService.setForegroundMessageFilterCallback: a view can suppress the in-app push
// overlay for messages it already displays itself — the open chat does this for its own chatId,
// otherwise a modal pops up on top of the message that just arrived in the thread.
let foregroundMessageFilter: ((data: PushData) => boolean) | null = null;

export function setForegroundMessageFilter(filter: (data: PushData) => boolean): void {
    foregroundMessageFilter = filter;
}

export function resetForegroundMessageFilter(): void {
    foregroundMessageFilter = null;
}

function acceptsForegroundMessage(data: PushData): boolean {
    return foregroundMessageFilter ? foregroundMessageFilter(data) : true;
}

// `currentToken` is what the BACKEND has confirmed, `pendingToken` what the device holds but could
// not hand over yet (PUT failed, or no account on the device at that moment).
let currentToken: string | null = null;
let pendingToken: string | null = null;
let firebaseProjectId = '';
let firebaseMessaging: import('firebase/messaging').Messaging | null = null;
let swRegistration: ServiceWorkerRegistration | null = null;
let initialized = false;

// TEMP QA: read-only peek at the module-level token state for the imprint debug panel. A device
// that never reached `register()` (e.g. Android <= 12, where the plugin reports `granted`
// unconditionally, so the opt-in modal never showed) holds neither token — which is otherwise
// invisible from the outside: the UI reports push as on and the system settings agree.
export function getPushDebugState(): { confirmedToken: string | null, pendingToken: string | null } {
    return { confirmedToken: currentToken, pendingToken };
}

// --- backend helpers ---

// Hands the device token to the backend. `currentToken` is only advanced once the PUT actually
// succeeded — marking it up front lost the token for the rest of the app session, because every
// later `registration` event carries the same value and was then filtered out as "already sent".
// That is what made push arrive only after a restart: FCM emits `registration` on its own via
// onNewToken (fresh install, token rotation), typically while the login screen is still up, so the
// PUT is skipped for want of an account — and the register() triggered by the opt-in later saw an
// unchanged token. The token now stays in `pendingToken` and is retried while push is enabled.
async function syncTokenWithBackend(token: string): Promise<void> {
    if (token === currentToken) {
        return;
    }
    pendingToken = token;

    const userStore = useUserStore();
    // All accounts on this device, not just the loaded profile (Angular
    // notification-preferences.service does the same) — hence the plural endpoint. Derived from
    // the stored JWTs, so this no longer depends on `GET /users` having completed, which is what
    // used to make the token vanish when the user logged in mid-session.
    const userIds = userStore.deviceAccountUserIds;
    if (!userIds.length) {
        return;
    }
    try {
        await apiFetch('/preferences/notifications/update-device-token-for-users', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token,
                userIds,
                firebaseProjectId,
            }),
        });
        currentToken = token;
        pendingToken = null;
    } catch (e) {
        console.error('[usePush] storeTokenInBackend failed', e);
    }
}

// Only for a device that has opted in — Angular gates the backend call on `enabled$` too, and a
// user who declined must not end up registered.
async function flushPendingToken(): Promise<void> {
    if (!pendingToken || localStorage.getItem(PUSH_STORAGE_KEY) !== 'true') {
        return;
    }
    await syncTokenWithBackend(pendingToken);
}

async function deleteTokenFromBackend(token: string) {
    try {
        await apiFetch(`/preferences/notifications/device-token/${token}`, { method: 'DELETE' });
    } catch (e) {
        console.error('[usePush] deleteTokenFromBackend failed', e);
    }
}

// --- web push ---

async function initWeb(config: FirebaseConfig) {
    if (!isWebPushCapable) {
        return;
    }

    const { getMessaging, isSupported, onMessage } = await import('firebase/messaging');
    const { initializeApp } = await import('firebase/app');

    // Angular WebPushHandler.init() gates everything on firebase's own isSupported().
    if (!(await isSupported())) {
        return;
    }

    const app = initializeApp(config);
    firebaseMessaging = getMessaging(app);

    // Only a service worker that registers AND becomes ready counts as "push supported"
    // (= Angular's supported$.next(true) in the .then(), .next(false) in the .catch()).
    try {
        const swUrl = `${import.meta.env.BASE_URL}firebase-messaging-sw.js`;
        const reg = await navigator.serviceWorker.getRegistration();
        swRegistration = reg ?? (await navigator.serviceWorker.register(swUrl));
        if (!swRegistration.pushManager) {
            throw new Error('Push messaging is not supported in this browser');
        }
        await navigator.serviceWorker.ready;
        supported.value = true;

        onMessage(firebaseMessaging, (payload) => {
            const data = payload.data as PushData | undefined;
            if (data?.appRoute && acceptsForegroundMessage(data)) {
                foregroundMessage.value = {
                    ...data,
                    // notification block provides the display title/body; fall back to data
                    // (data-only messages) then empty. Spread first so these win, not get clobbered.
                    title: payload.notification?.title ?? data.title ?? '',
                    body: payload.notification?.body ?? data.body ?? '',
                };
            }
        });
    } catch (e) {
        console.error('[usePush] SW registration failed', e);
        supported.value = false;
    }
}

async function enableWeb() {
    if (!firebaseMessaging) {
        return;
    }
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(firebaseMessaging, { serviceWorkerRegistration: swRegistration ?? undefined });
    if (token) {
        await syncTokenWithBackend(token);
    }
}

async function disableWeb() {
    if (!firebaseMessaging) {
        return;
    }
    const { deleteToken } = await import('firebase/messaging');
    if (currentToken) {
        await deleteTokenFromBackend(currentToken);
    }
    await deleteToken(firebaseMessaging);
    currentToken = null;
    pendingToken = null;
}

// --- capacitor push ---

function mapNativePermission(receive: string): NotificationPermission {
    return receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'default';
}

// Angular: clearApplicationIconBadge. There is no separate badge API — clearing the delivered
// notifications is what resets the iOS badge. Guarded by the permission check and swallowing the
// error, because on a fresh install iOS rejects this with "CapacitorDidRegisterForRemoteNotifications
// not called". Angular runs it at the end of both listeners; doing it in one place here covers the
// same ground without the duplicate call it makes on the tap path.
async function clearDeliveredNotifications(): Promise<void> {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    try {
        if ((await PushNotifications.checkPermissions()).receive !== 'granted') {
            return;
        }
        await PushNotifications.removeAllDeliveredNotifications();
    } catch (e) {
        console.warn('[usePush] clearing delivered notifications failed', e);
    }
}

// Deliberate deviation from Angular, whose clearApplicationIconBadge() is not platform-gated and so
// empties the Android tray on every resume as well. Android has no numeric badge to reset — the
// backend sets only `aps.badge`, and the launcher dot follows the tray — so on resume the call buys
// nothing there and silently discards notifications the user never opened. The listener paths below
// stay platform-neutral: clearing on tap or on an in-app arrival is what Angular does and is sane on
// both platforms.
async function clearBadgeOnResume(): Promise<void> {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'ios') {
        return;
    }
    await clearDeliveredNotifications();
}

// Angular: CapacitorPushHandlerService.openNotification. A payload carrying an institutionId goes
// through the institution redirect, which switches to the account that owns the notification before
// forwarding — InstitutionRedirectView reads exactly this `target` query param. Without an id the
// route is entered directly; the locale prefix is added by the router's catch-all redirect.
function openNotification(router: Router, data: PushData): void {
    if (!data.appRoute) {
        return;
    }
    if (data.institutionId) {
        router.push(`/institution/${data.institutionId}?target=${encodeURIComponent(data.appRoute)}`);
    } else {
        router.push(data.appRoute);
    }
}

async function initCapacitor(config: PushFirebaseConfig, router: Router) {
    firebaseProjectId = config.projectId;
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Reflect the REAL native permission: the web Notification API is absent/irrelevant in the
    // Capacitor WebView, so `permission` must come from Capacitor, not `Notification.permission`.
    permission.value = mapNativePermission((await PushNotifications.checkPermissions()).receive);

    // Angular capacitor-push-handler.service.ts:76 — `appState.reload()` (Fehlerseite „Daten
    // aktualisieren") muss die nativen Listener abräumen, bevor die WebView neu lädt.
    useAppStateStore().addOnTerminateCallback(() => PushNotifications.removeAllListeners());

    PushNotifications.addListener('registration', (tokenObj) => {
        if (tokenObj.value) {
            void syncTokenWithBackend(tokenObj.value);
        }
    });

    PushNotifications.addListener('registrationError', (err) => {
        console.error('[usePush] Capacitor registration error', err);
    });

    // Tap on a notification in the system tray — app backgrounded or cold-started. Nothing consumed
    // this event before, so the app merely came to the foreground on whatever route it was left on.
    // (The in-app path works because App.vue navigates from `foregroundMessage`, which only the
    // foreground listener below ever fills.) Registering here — i.e. after `await initApp()` — is
    // still safe for a cold start: Capacitor retains the event until a listener exists
    // (PushNotificationsHandler.swift emits it with retainUntilConsumed: true).
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        if (notification.data?.appRoute) {
            openNotification(router, notification.data as PushData);
        }
        clearDeliveredNotifications();
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
        if (notification.data?.appRoute && acceptsForegroundMessage(notification.data as PushData)) {
            const data: PushData = {
                ...notification.data,
                // same ordering as the web path: notification title/body win over data.
                title: notification.title ?? notification.data.title ?? '',
                body: notification.body ?? notification.data.body ?? '',
            };
            // Angular branches on AppStateService.isVisible: the in-app modal only makes sense while
            // the app is actually on screen. This listener can also fire while backgrounded (Android
            // data messages), and the modal would then surface on the next resume, detached from the
            // notification that caused it — so treat that like a tap and navigate instead.
            if (useAppStateStore().isVisible) {
                foregroundMessage.value = data;
            } else {
                openNotification(router, data);
            }
        }
        clearDeliveredNotifications();
    });
}

async function enableCapacitor() {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    permission.value = mapNativePermission((await PushNotifications.requestPermissions()).receive);
    if (permission.value === 'granted') {
        await PushNotifications.register();
    }
}

async function disableCapacitor() {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    if (currentToken) {
        await deleteTokenFromBackend(currentToken);
    }
    await PushNotifications.unregister();
    currentToken = null;
    pendingToken = null;
}

// Did the user opt in — in the Vue app, or previously in the Angular one? On the first Vue run our
// key is unset, so inherit the Angular decision and persist it under our key (once). Without this
// the stale Angular token — bound to the old service worker / app install — is never replaced, so
// pushes silently stop until a manual off→on toggle.
async function resolveEnabledFlag(): Promise<string | null> {
    const stored = localStorage.getItem(PUSH_STORAGE_KEY);
    if (stored !== null) {
        return stored;
    }

    const legacy = await readLegacyEnabledFlag();
    if (legacy !== null) {
        localStorage.setItem(PUSH_STORAGE_KEY, legacy);
        enabled.value = legacy === 'true';
    }
    return legacy;
}

// --- public API ---

export function usePush() {
    async function init(config: PushFirebaseConfig, router: Router) {
        if (initialized) {
            return;
        }
        initialized = true;
        firebaseProjectId = config.projectId;

        if (isNative) {
            await initCapacitor(config, router);
        } else if (isWebConfig(config)) {
            await initWeb(config);
        } else {
            console.warn('[usePush] no web push: the Firebase config has only a project id');
        }

        // Register on the LOGIN, not on app start (Angular app.component: `mainUserId$` +
        // `pairwise()` → register when `newUserId && !oldUserId`). init() runs once per app
        // process, so registering inline here would only ever cover users who are already logged
        // in at boot — anyone logging in later in the same session (the normal case right after an
        // update that resets the session) would never get a token registered.
        // `immediate` covers the already-logged-in case; keying on the JWT user id rather than
        // currentUser ignores multi-user profile switches, like Angular does.
        const userStore = useUserStore();
        watch(
            () => userStore.mainUserId,
            async (userId, previousUserId) => {
                if (!userId || previousUserId) {
                    return;
                }
                // Angular app.component registers `pushService.unregister()` as a logout callback on
                // exactly this transition. Hanging it off the store (instead of the logout screen)
                // covers the app menus, which call `userStore.logout()` directly — without it the
                // device token stayed registered and the phone kept receiving pushes for the account
                // that had just logged out.
                userStore.addLogoutCallback(() => unregister());

                const stored = await resolveEnabledFlag();
                // An explicit opt-out is the ONLY thing that leaves this device unregistered.
                if (stored === 'false') {
                    return;
                }
                // Undecided must register too whenever the OS already allows notifications:
                // KgrAskForPushPermission hides itself on `permission === 'granted'`, so on such a
                // device the prompt never appears and nothing else would ever call enable(). The
                // device then reports push as on (`isPushEnabledStored()` is `!== 'false'`, i.e. on
                // unless explicitly off) while holding no FCM token at all — active in the UI and in
                // the system settings, yet nothing is ever delivered. A device whose permission is
                // still 'default' keeps waiting for the prompt, as before.
                if (stored === 'true' || permission.value === 'granted') {
                    await enable();
                }
            },
            { immediate: true },
        );

        // A token the backend never accepted (offline moment, 5xx, or no account on the device yet)
        // would otherwise sit unsent until the next app start — which is what made push arrive only
        // after closing and reopening the app.
        const appState = useAppStateStore();
        watch(
            () => appState.isVisible,
            (isVisible) => {
                if (isVisible) {
                    void flushPendingToken();
                }
            },
        );

        // Angular PushService.init(): `appState.show` -> delay(1000) -> clearApplicationIconBadge().
        // Opening the app from the home screen icon is the normal way to read a notification, and
        // nothing on that path clears the badge that `aps.badge` set — only the two push listeners
        // do, so an untapped notification left iOS stuck on "1" forever. `immediate` because
        // Angular's `show` is a BehaviorSubject and fires on subscribe, i.e. on a cold start too —
        // which is exactly the common case here, since `isVisible` already starts out true.
        if (isNative) {
            watch(
                () => appState.isVisible,
                (isVisible) => {
                    if (isVisible) {
                        setTimeout(() => void clearBadgeOnResume(), 1000);
                    }
                },
                { immediate: true },
            );
        }
    }

    async function requestPermission(): Promise<NotificationPermission> {
        if (isNative) {
            const { PushNotifications } = await import('@capacitor/push-notifications');
            permission.value = mapNativePermission((await PushNotifications.requestPermissions()).receive);
            return permission.value;
        }
        if (typeof Notification === 'undefined') {
            return 'default';
        }
        // eslint-disable-next-line compat/compat -- Notification API used in native apps via Capacitor, not Safari
        const result = await Notification.requestPermission();
        permission.value = result;
        return result;
    }

    async function enable() {
        // Native: enableCapacitor() does the native permission request + register and updates
        // `permission`. Do NOT gate on the web Notification API here — it's absent in the WebView,
        // which previously made this return early so push could never (re)register natively.
        if (isNative) {
            await enableCapacitor();
        } else {
            const perm = permission.value === 'default' ? await requestPermission() : permission.value;
            if (perm === 'granted') {
                await enableWeb();
            }
        }
        localStorage.setItem(PUSH_STORAGE_KEY, permission.value === 'granted' ? 'true' : 'false');
        enabled.value = permission.value === 'granted';
    }

    // Angular `PushService.unregister()`: loescht den Device-Token im Backend und meldet den
    // Handler ab — **ohne** das enabled-Flag anzufassen. Genau das haengt dort als
    // `userService.addLogoutCallback(...)` am Logout: der Nutzer bleibt fuer Push angemeldet, aber
    // DIESES Geraet erhaelt keine Zustellungen mehr fuer das abgemeldete Konto. Muss VOR dem
    // Loeschen des Auth-Tokens laufen, sonst geht der DELETE unauthentifiziert raus.
    async function unregister() {
        if (isNative) {
            await disableCapacitor();
        } else {
            await disableWeb();
        }
    }

    async function disable() {
        localStorage.setItem(PUSH_STORAGE_KEY, 'false');
        enabled.value = false;
        if (isNative) {
            await disableCapacitor();
        } else {
            await disableWeb();
        }
    }

    // Same entry as the notification tap (Angular ForegroundPushMessageComponent.onOpenClick):
    // routing through the institution redirect switches to the account the message belongs to.
    function openMessage(router: Router, data: PushData) {
        openNotification(router, data);
    }

    return {
        supported,
        permission,
        enabled,
        foregroundMessage,
        init,
        enable,
        disable,
        unregister,
        requestPermission,
        openMessage,
    };
}
